import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import CartStore from "../../stores/CartStore";
import { priceFormatter } from "../../utils/helpers";
import CartExpiryBanner from "../cart/CartExpiryBanner";
import toast from "../../utils/toast";

type Props = {
    cartStore: CartStore;
    donations?: { [key: number]: number };
};

const CartSummary = observer(({ cartStore, donations = {} }: Props) => {
    const [promoCode, setPromoCode] = React.useState("");

    const ticketSubtotal = cartStore.ticketSubtotal ?? 0;
    const subtotal = cartStore.subtotal ?? 0;
    const authFee = cartStore.authFee ?? 0;
    const cartTotal = cartStore.total ?? 0;
    const donationsTotal = Object.values(donations).reduce((a, b) => a + b, 0);
    const total = cartTotal + donationsTotal;

    const showSubtotal = subtotal !== cartTotal;
    const hasItems = cartStore.hasItems;

    // Don't render if cart is empty
    if (!hasItems) {
        return null;
    }

    return (
        <Card sx={{ position: "sticky", top: 16, height: "fit-content" }}>
            <CardContent>
                <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
                    Order Summary
                </Typography>

                {cartStore.cartExpiresInSeconds && (
                    <Box sx={{ mb: 2 }}>
                        <CartExpiryBanner
                            expiresInSeconds={cartStore.cartExpiresInSeconds}
                            onTimeUp={() => {
                                toast.error("Cart has expired.");
                                cartStore.clearCart(false);
                            }}
                        />
                    </Box>
                )}

                {/* Items List */}
                <Box sx={{ mb: 2 }}>
                    {cartStore.packageItemsGrouped.map((group) => {
                        const groupTotal = group.items.reduce(
                            (sum, item) =>
                                sum + cartStore.packageItemTotal(item),
                            0,
                        );
                        const groupTotalWithoutFees = group.items.reduce(
                            (sum, item) =>
                                sum +
                                cartStore.packageItemTotal(item) -
                                item.serviceFees.reduce(
                                    (feeSum, fee) => feeSum + fee.amount,
                                    0,
                                ),
                            0,
                        );

                        return (
                            <Box
                                key={group.eventPackage.id}
                                sx={(sx) => ({
                                    mb: 2,
                                    p: 2,
                                    bgcolor: sx.palette.background.default,
                                    borderRadius: 1,
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {group.eventPackage.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Quantity: {group.items.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {priceFormatter(groupTotal)}
                                        </Typography>
                                        {groupTotalWithoutFees !==
                                            groupTotal && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                (
                                                {priceFormatter(
                                                    groupTotalWithoutFees,
                                                )}{" "}
                                                +{" "}
                                                {priceFormatter(
                                                    groupTotal -
                                                        groupTotalWithoutFees,
                                                )}{" "}
                                                fees)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}

                    {cartStore.seatmapItemsGrouped.map((group) => {
                        const sectionItems = Object.values(group.sections).flatMap((s) => s.items);
                        const allItems = [...group.seats, ...sectionItems];
                        const groupTotal = allItems.reduce(
                            (sum, item) => sum + cartStore.itemTotal(item),
                            0,
                        );
                        const groupTotalWithoutFees = allItems.reduce(
                            (sum, item) =>
                                sum +
                                cartStore.itemTotal(item) -
                                item.serviceFees.reduce(
                                    (feeSum, fee) => feeSum + fee.amount,
                                    0,
                                ),
                            0,
                        );

                        return (
                            <Box
                                key={group.eventSeatmap.id}
                                sx={(sx) => ({
                                    mb: 2,
                                    p: 2,
                                    bgcolor: sx.palette.background.default,
                                    borderRadius: 1,
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {group.eventSeatmap.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Quantity: {allItems.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {priceFormatter(groupTotal)}
                                        </Typography>
                                        {groupTotalWithoutFees !==
                                            groupTotal && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                (
                                                {priceFormatter(
                                                    groupTotalWithoutFees,
                                                )}{" "}
                                                +{" "}
                                                {priceFormatter(
                                                    groupTotal -
                                                        groupTotalWithoutFees,
                                                )}{" "}
                                                fees)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}

                    {cartStore.assignedSeatingItemsGrouped.map((group) => {
                        const groupTotal = group.items.reduce(
                            (sum, item) => sum + cartStore.itemTotal(item),
                            0,
                        );
                        const groupTotalWithoutFees = group.items.reduce(
                            (sum, item) =>
                                sum +
                                cartStore.itemTotal(item) -
                                item.serviceFees.reduce(
                                    (feeSum, fee) => feeSum + fee.amount,
                                    0,
                                ),
                            0,
                        );

                        return (
                            <Box
                                key={group.department.id}
                                sx={(sx) => ({
                                    mb: 2,
                                    p: 2,
                                    bgcolor: sx.palette.background.default,
                                    borderRadius: 1,
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {group.department.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Quantity: {group.items.length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {priceFormatter(groupTotal)}
                                        </Typography>
                                        {groupTotalWithoutFees !==
                                            groupTotal && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                (
                                                {priceFormatter(
                                                    groupTotalWithoutFees,
                                                )}{" "}
                                                +{" "}
                                                {priceFormatter(
                                                    groupTotal -
                                                        groupTotalWithoutFees,
                                                )}{" "}
                                                fees)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}

                    {cartStore.gaItems.map((item) => {
                        // GACartItem already has totalAmount calculated
                        const itemTotal = item.totalAmount;
                        const itemTotalWithoutFees =
                            itemTotal -
                            item.quantity *
                                item.serviceFees.reduce(
                                    (feeSum, fee) => feeSum + fee.amount,
                                    0,
                                );

                        return (
                            <Box
                                key={item.department.id}
                                sx={(sx) => ({
                                    mb: 2,
                                    p: 2,
                                    bgcolor: sx.palette.background.default,
                                    borderRadius: 1,
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {item.department.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Quantity: {item.quantity}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {priceFormatter(itemTotal)}
                                        </Typography>
                                        {itemTotalWithoutFees !== itemTotal && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                (
                                                {priceFormatter(
                                                    itemTotalWithoutFees,
                                                )}{" "}
                                                +{" "}
                                                {priceFormatter(
                                                    itemTotal -
                                                        itemTotalWithoutFees,
                                                )}{" "}
                                                fees)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Promo Code Form */}
                {cartStore.shouldShowPromoCodeForm && (
                    <Box sx={{ mb: 2 }}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!cartStore.promoCode) {
                                    cartStore.applyPromoCode(promoCode);
                                    setPromoCode("");
                                } else {
                                    cartStore.removePromoCode();
                                }
                            }}
                        >
                            <Grid container spacing={1}>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Promo Code"
                                        fullWidth
                                        size="small"
                                        color="primary"
                                        value={
                                            cartStore.promoCode || promoCode
                                        }
                                        disabled={
                                            !!cartStore.promoCode ||
                                            cartStore.loading
                                        }
                                        onChange={(e) => {
                                            setPromoCode(e.target.value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Button
                                        variant="contained"
                                        color={
                                            cartStore.promoCode
                                                ? "error"
                                                : "primary"
                                        }
                                        fullWidth
                                        type="submit"
                                        disabled={cartStore.loading}
                                        sx={{ height: "100%" }}
                                    >
                                        {cartStore.promoCode
                                            ? "Clear"
                                            : "Apply"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Price Breakdown */}
                <Grid container spacing={1}>
                    <Grid item xs={8}>
                        <Typography variant="body2">Ticket Subtotal</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" textAlign="right">
                            {priceFormatter(ticketSubtotal)}
                        </Typography>
                    </Grid>

                    {authFee !== 0 && (
                        <>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    Transaction Fee
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(authFee)}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {cartStore.deliveryMethod && (
                        <>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    {cartStore.deliveryMethod.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(
                                        cartStore.deliveryMethod.amount,
                                    )}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {cartStore.customAmount > 0 && (
                        <>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    Custom Amount
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(cartStore.customAmount)}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {showSubtotal && (
                        <>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    Subtotal
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(subtotal)}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {cartStore.taxes.map((tax) => (
                        <React.Fragment key={tax.name}>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    {tax.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(tax.amount)}
                                </Typography>
                            </Grid>
                        </React.Fragment>
                    ))}

                    {donationsTotal > 0 && (
                        <>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    Donations
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(donationsTotal)}
                                </Typography>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={8}>
                        <Typography variant="h6" fontWeight={700}>
                            Total
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            textAlign="right"
                        >
                            {priceFormatter(total)}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
});

export default CartSummary;
